<template>
    <div>
        <page-header :title="title" :items="items" />

        <div v-if="!editMode || (editMode && tag.id)" class="row">
            <div class="col-xl-9 mb-3">
                <div class="card">
                    <div class="card-body">
                        <add-edit-form
                            v-model="tag"
                            :edit-mode="editMode"
                            :validation="$v"
                            :server-errors="serverErrors"
                            @on-name-change="onNameChange"
                        />
                    </div>
                </div>
            </div>

            <div class="col-xl-3 mb-3">
                <div class="card">
                    <div class="card-body">
                        <div :class="{ 'mb-3': editMode }">
                            <a
                                v-if="editMode"
                                :href="previewUrl"
                                class="btn btn-primary btn-static waves-effect waves-light m-1"
                                target="_blank"
                                @click="onPreviewButtonClick"
                            >
                                <i class="far fa-eye" />
                                Preview
                            </a>
                            <button
                                class="btn btn-success btn-static waves-effect waves-light m-1"
                                @click="submitHandler"
                            >
                                <i class="fas fa-save" />
                                Save
                            </button>
                        </div>

                        <item-information
                            :key="informationKey"
                            :edit-mode="editMode"
                            :sections="sections"
                            :item="tag"
                            :slug="tag.slug"
                            :options="{ slugBasicUrl: 'article-tags' }"
                            :slug-error="slugError"
                            @meta-change="onMetaChange"
                            @on-slug-change="onSlugChange"
                            @on-delete-confirm="onDeleteConfirm"
                        />
                    </div>
                </div>
            </div>
        </div>
        <skeleton-loader v-else />
    </div>
</template>

<script>
import { mapActions } from 'vuex';
import { required } from 'vuelidate/lib/validators';
import { StatusCodes as HTTP } from 'http-status-codes';
import { frontendUrl } from '@/config';
import AddEditForm from '@/components/article-tags/AddEditForm';
import ItemInformation from '@/components/elements/ItemInformation';

export default {
    page: {
        title: 'Manage Article Tag'
    },

    validations: {
        tag: {
            name: {
                required
            },
            slug: {
                required
            }
        }
    },

    components: {
        AddEditForm,
        ItemInformation
    },

    data() {
        const { id = null } = this.$route.params;

        return {
            title: id ? 'Edit article tag' : 'Add article tag',
            items: [
                {
                    text: 'Aguda',
                    to: '/'
                },
                {
                    text: 'Article Tags',
                    to: '/article-tags'
                },
                {
                    text: id ? 'Edit' : 'Add',
                    active: true
                }
            ],
            id,
            editMode: !!id,
            tag: {
                meta: {}
            },
            informationKey: 0,
            serverErrors: [],
            tagValueStringify: ''
        };
    },

    computed: {
        sections() {
            const sections = {
                permalink: true,
                meta: true
            };

            if (this.editMode) {
                sections.status = true;
            }

            return sections;
        },

        previewUrl() {
            return `${frontendUrl}/article-tags/${this.tag.slug}`;
        },

        slugError() {
            return this.$getValidationErrorMessageHandler.handle(
                this.$v.tag,
                this.serverErrors,
                'slug'
            );
        }
    },

    async created() {
        if (this.editMode) {
            try {
                this.tag = await this.getTag(this.id);
                this.tagValueStringify = JSON.stringify(this.tag);
            } catch (error) {
                console.error(error);

                this.$toasterError();
            }
        }
    },

    methods: {
        ...mapActions({
            addTag: 'articleTags/store',
            deleteTag: 'articleTags/destroy',
            getTag: 'articleTags/show',
            updateTag: 'articleTags/update'
        }),

        async submitHandler() {
            this.serverErrors = [];

            this.$v.$touch();

            if (this.$v.$invalid) {
                return;
            }

            try {
                if (!this.editMode) {
                    const added = await this.addTag(this.tag);

                    this.$router.push(`/article-tags/${added.id}/edit`);
                } else {
                    const { updatedAt } = await this.updateTag({
                        id: this.id,
                        data: this.tag
                    });

                    this.tag.updatedAt = updatedAt;
                    this.tagValueStringify = JSON.stringify(this.tag);

                    this.$toaster('Tag updated!');
                }
            } catch (error) {
                const { response } = error;

                if (response) {
                    const { data, status } = response;

                    if (status === HTTP.BAD_REQUEST && data.errors) {
                        this.serverErrors = data.errors;

                        this.$toasterError('Recheck your form.');

                        return;
                    }
                }

                console.error(error);

                this.$toasterError();
            }
        },

        async onDeleteConfirm() {
            try {
                await this.deleteTag(this.id);

                this.$router.push('/article-tags');
            } catch (error) {
                const { response } = error;

                if (response) {
                    const { data, status } = response;

                    if (status === HTTP.UNPROCESSABLE_ENTITY && data) {
                        this.$toasterError(data);

                        return;
                    }
                }

                console.error(error);

                this.$toasterError();
            }
        },

        onPreviewButtonClick(e) {
            if (this.tagValueStringify !== JSON.stringify(this.tag)) {
                e.preventDefault();

                this.$toaster('Please save changes before preview!', {
                    title: 'Warning!',
                    variant: 'warning'
                });
            }
        },

        onMetaChange(meta) {
            this.tag.meta = { ...meta };
        },

        onNameChange() {
            if (this.editMode) {
                return;
            }

            this.onSlugChange(this.tag.name);
            this.informationKey++;
        },

        onSlugChange(slug) {
            this.tag.slug = slug;
        }
    }
};
</script>
